import { useEffect, useRef } from 'react'
import { posterMode } from '@/render/poster'

/**
 * A number that visibly moves.
 *
 * It is one text node, not a stack of rolling digit columns, and that is a deliberate trade. A
 * digit-strip odometer has to render all ten digits per column and hide nine of them behind
 * `overflow: hidden`, which leaves the page's own text — what `document.body.innerText` returns,
 * what a visitor copies, what the screenshot script asserts on — a wall of `0123456789`. The
 * evidence this project keeps asking for is the page's text; a decoration that corrupts it costs
 * more than it is worth. So the roll is a kick instead: the number pops up a couple of pixels and
 * flashes its accent every time it changes, which at two ticks a second and twenty presses a
 * second reads as a counter that is alive.
 *
 * The kick is a Web Animations object created once per mount and restarted by rewinding it, so a
 * tab being hammered allocates nothing per press. `prefers-reduced-motion` drops the kick and
 * keeps the number.
 */
export function Odometer({
  value,
  size = 'sm',
  ariaLabel,
  ready = true,
}: {
  value: number
  size?: 'sm' | 'lg'
  ariaLabel: string
  /** Before `hello` there is no honest number; a zero that never moves reads as a broken site. */
  ready?: boolean
}) {
  const ref = useRef<HTMLSpanElement>(null)
  const kick = useRef<Animation | null>(null)
  const previous = useRef(value)

  useEffect(() => {
    const node = ref.current
    const up = value > previous.current
    previous.current = value
    if (!node || !up || !ready) return
    if (typeof node.animate !== 'function') return
    if (posterMode()) return

    kick.current ??= node.animate(
      [
        { transform: 'translateY(0)', filter: 'brightness(1)' },
        { transform: 'translateY(-0.08em)', filter: 'brightness(1.35)', offset: 0.3 },
        { transform: 'translateY(0)', filter: 'brightness(1)' },
      ],
      { duration: 260, easing: 'cubic-bezier(0.33, 1, 0.68, 1)' },
    )
    // Restart rather than replace: a press every 50 ms would otherwise leave a queue of
    // animations fighting over the same transform.
    kick.current.currentTime = 0
    kick.current.play()
  }, [value, ready])

  return (
    <span
      ref={ref}
      role="img"
      aria-label={ready ? `${group(value)} ${ariaLabel}` : `${ariaLabel}: waiting for live data`}
      className={size === 'lg' ? 'mha-odo mha-odo--lg' : 'mha-odo'}
    >
      {ready ? group(value) : '—'}
    </span>
  )
}

/**
 * Narrow no-break spaces rather than commas: the display font's comma is a blob at this size, and
 * a group separator that can wrap would break a number across two lines.
 */
export function group(value: number): string {
  return Math.max(0, Math.trunc(value))
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}
